// src/components/AdvisorConnectForm.js
//
// Collects and validates the "Connect with an advisor" contact fields
// (name, email, phone, marketing consent). Does NOT call the
// advisor-connect edge function itself.
//
// Previously this component posted straight to advisor-connect on submit.
// That stopped being workable once anonymous submissions needed an OTP
// step in between (collect contact info -> verify email -> claim-session
// -> THEN advisor-connect, so the lead lands on an authenticated,
// tax_subject-linked session). Rather than fork the form into two
// versions, this is now a pure "collect + validate" step — onCollected(fields)
// hands the validated values up, and ResultCTA.js (the only remaining
// caller) decides what happens next: submit immediately (already
// authenticated / partner-linked, no OTP needed) or hold the values until
// after OTP + claim-session succeed. See src/lib/advisorConnect.js for the
// actual submission call.
//
// initialEmail + emailLocked: for authenticated users we already have a
// verified account email, so ResultCTA passes it in pre-filled and
// non-editable — one less field to type, and keeps the submitted lead's
// contact_email consistent with the account it's now tied to.

import React, { useState } from 'react'
import MarketingConsentCheckbox from './MarketingConsentCheckbox'
import { validateEmail } from '../lib/emailValidation'
import { PHONE_COUNTRIES, DEFAULT_COUNTRY_CODE, getCountry, validatePhoneForCountry } from '../lib/phoneCountries'

// hideMarketingConsent (new): true when ResultCTA.js already knows the
// person gave a real answer to this question earlier in the same session
// (the primary "save and continue" flow's own checkbox, tracked via
// hasGivenMarketingConsent there). Prevents asking the same question
// twice with no memory of the first answer, AND — just as important —
// prevents sending a fresh `false` for a question that was never actually
// re-asked. That second part matters even though advisor-connect/index.ts
// now independently refuses to overwrite an existing answer (the real
// fix, for the case this prop can't see — a returning user in a fresh
// session): this prop is the same-session UX layer on top, not the only
// safeguard.

export default function AdvisorConnectForm({ partnerName, answersLabel = 'triage', submitLabel = 'Continue →', onCollected, initialEmail = '', emailLocked = false, hideMarketingConsent = false }) {
  const [name,             setName]             = useState('')
  const [email,            setEmail]            = useState(initialEmail)
  const [phoneCountry,     setPhoneCountry]     = useState(DEFAULT_COUNTRY_CODE)
  const [phoneDigits,      setPhoneDigits]      = useState('')
  const [marketingConsent, setMarketingConsent] = useState(false)
  const [error,            setError]            = useState(null)

  function handleSubmit() {
    if (!name.trim())                   { setError('Please enter your name.'); return }
    // Locked email is the account email, already verified by Supabase Auth.
    const checked = emailLocked ? { ok: true, email: email.trim() } : validateEmail(email)
    if (!checked.ok)                    { setError(checked.error); return }
    // Empty national number is always valid — the field is optional (D2).
    // The length check only runs when something was actually typed.
    if (phoneDigits) {
      const phoneChecked = validatePhoneForCountry(phoneCountry, phoneDigits)
      if (!phoneChecked.ok)              { setError(phoneChecked.error); return }
    }
    setError(null)
    // Combined dial-code + digits string (e.g. "+12125550134"), what
    // ticket 008's tel: link on the Case Brief page needs. "Other" has no
    // dial code, so its stored value is digits only, with no "+" prefix.
    const phone = phoneDigits
      ? (phoneCountry === 'OTHER' ? phoneDigits : `${getCountry(phoneCountry).dialCode}${phoneDigits}`)
      : null
    onCollected({
      name:             name.trim(),
      email:            checked.email,
      phone,
      // undefined (not false) when hidden — this field means "no new
      // answer was given this submission," not "the person declined."
      // advisorConnect.js only forwards marketing_consent to the edge
      // function when this is an actual boolean; undefined omits the key
      // entirely rather than coercing to false.
      marketingConsent: hideMarketingConsent ? undefined : marketingConsent,
    })
  }

  return (
    <div className="tr-connect-form">
      <input type="text"  className="app-input" placeholder="Your name"              value={name}  onChange={e => { setName(e.target.value);  setError(null) }} />
      <input
        type="email"
        className={`app-input${emailLocked ? ' app-input--locked' : ''}`}
        placeholder="Email address"
        value={email}
        onChange={e => { if (!emailLocked) { setEmail(e.target.value); setError(null) } }}
        readOnly={emailLocked}
        disabled={emailLocked}
      />
      {emailLocked && <p className="tr-connect-locked-hint">Using your account email</p>}
      <div className="tr-connect-phone-row">
        <select
          className="app-input tr-connect-phone-select"
          value={phoneCountry}
          onChange={e => { setPhoneCountry(e.target.value); setError(null) }}
        >
          {PHONE_COUNTRIES.map(c => (
            <option key={c.code} value={c.code}>
              {c.dialCode ? [c.flag, c.dialCode].filter(Boolean).join(' ') : c.name}
            </option>
          ))}
        </select>
        <input
          type="tel"
          className="app-input"
          placeholder={getCountry(phoneCountry).placeholder}
          value={phoneDigits}
          onChange={e => { setPhoneDigits(e.target.value.replace(/\D/g, '')); setError(null) }}
        />
      </div>
      <p className="tr-connect-consent">
        By submitting, you authorise HolaSam to share your {answersLabel} answers and contact details with{' '}
        <strong>{partnerName || 'a partner advisor'}</strong> so they can follow up with you directly.{' '}
        <a href="/privacy" className="disclaimer-link">Privacy policy</a>.
      </p>
      {!hideMarketingConsent && (
        <MarketingConsentCheckbox checked={marketingConsent} onChange={setMarketingConsent} />
      )}
      {error && <div className="triage-email-error">{error}</div>}
      <button className="btn-primary tr-panel-btn" onClick={handleSubmit}>
        {submitLabel}
      </button>
    </div>
  )
}